import React, { useState } from 'react';
import { CTASection } from '../../components/CTASection';
import { Palette, CheckCircle, Sparkles, Award, Zap, Users, Target, Lightbulb, ChevronDown } from 'lucide-react';
export function GraphicDesign() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const services = [{
    icon: '🎉',
    title: 'Event Banners',
    description: 'Bright, bold designs that capture the spirit of your celebration'
  }, {
    icon: '👕',
    title: 'Custom Apparel Graphics',
    description: "Unique designs for t-shirts, hoodies, and more that reflect your brand's personality"
  }, {
    icon: '🏆',
    title: 'Promotional Materials',
    description: 'Flyers, brochures, and business cards that leave a lasting impression'
  }, {
    icon: '🎯',
    title: 'Brand Identity',
    description: 'Comprehensive branding packages that tell your story and connect with your audience'
  }];
  const benefits = [{
    icon: <Lightbulb className="w-6 h-6" data-id="element-1448" />,
    title: 'Your Vision, Our Creation',
    description: 'Every great idea deserves to shine—we bring your imagination to life'
  }, {
    icon: <Users className="w-6 h-6" data-id="element-1449" />,
    title: 'In-House Design Team',
    description: 'Talented graphic designers ready to help you every step of the way'
  }, {
    icon: <Zap className="w-6 h-6" data-id="element-1450" />,
    title: 'One-Stop Shop',
    description: 'From concept to production, everything done under one roof'
  }, {
    icon: <Award className="w-6 h-6" data-id="element-1451" />,
    title: 'Logo YOUR Life',
    description: 'Eye-catching logos to stunning promotional materials—we do it all'
  }];
  const capabilities = ['Logo design & refinement', 'Brand identity development', 'Custom apparel graphics', 'Event banners & signage', 'Promotional materials', 'Business cards & stationery', 'Marketing collateral', 'Social media graphics'];
  return <div className="flex flex-col w-full" data-id="element-1452">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1453">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1454">
          <div className="flex justify-center mb-6" data-id="element-1455">
            <Palette className="w-16 h-16 text-white" data-id="element-1456" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1457">
            Graphic Design
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1458">
            Bringing your vision to life. From eye-catching logos to stunning
            promotional materials, our in-house design team turns your ideas
            into visual magic.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1459">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1460">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1461">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1462">
              Your Imagination, Our Creation
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1463">
              <p data-id="element-1464">
                At Wear Haus Designs, we believe that every great idea deserves
                to shine! Whether you already have a logo that needs a little
                sprucing up, are starting from scratch, or want to enhance your
                brand for better visibility, our talented in-house graphic
                design team is here to help you every step of the way.
              </p>
              <p data-id="element-1465">
                With everything done in-house, we make it easy for you to{' '}
                <span className="font-bold text-wh-pink" data-id="element-1466">Logo YOUR LIFE!</span>{' '}
                Our designers have crafted everything from eye-catching logos
                and vibrant event banners to stunning promotional materials.
                Here are just a few of the projects we've brought to life:
              </p>
            </div>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1467">
            {services.map((service, index) => <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1468">
                <div className="text-4xl mb-4" data-id="element-1469">{service.icon}</div>
                <h3 className="text-2xl font-bold text-wh-teal mb-3" data-id="element-1470">
                  {service.title}
                </h3>
                <p className="text-gray-700" data-id="element-1471">{service.description}</p>
              </div>)}
          </div>

          {/* Benefits Grid */}
          <div className="mb-16" data-id="element-1472">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1473">
              Why Choose Our Design Team?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1474">
              {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1475">
                  <div className="flex items-start gap-4" data-id="element-1476">
                    <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1477">
                      {benefit.icon}
                    </div>
                    <div data-id="element-1478">
                      <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1479">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-1480">{benefit.description}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Capabilities */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1481">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1482">
              One-Stop Shop for All Your Design Needs
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-1483">
              {capabilities.map((capability, index) => <div key={index} className="flex items-center gap-3" data-id="element-1484">
                  <CheckCircle className="w-6 h-6 text-wh-yellow flex-shrink-0" data-id="element-1485" />
                  <span className="text-lg" data-id="element-1486">{capability}</span>
                </div>)}
            </div>
          </div>

          {/* Process Highlight */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16" data-id="element-1487">
            <div className="bg-white rounded-xl p-8 border-2 border-wh-pink text-center" data-id="element-1488">
              <div className="w-16 h-16 bg-wh-pink rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-1489">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1490">
                Share Your Vision
              </h3>
              <p className="text-gray-700" data-id="element-1491">
                Tell us about your project, brand, and goals. We'll listen and
                ask the right questions.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-teal text-center" data-id="element-1492">
              <div className="w-16 h-16 bg-wh-teal rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4" data-id="element-1493">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1494">
                We Create Magic
              </h3>
              <p className="text-gray-700" data-id="element-1495">
                Our talented designers craft stunning visuals that bring your
                ideas to life.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border-2 border-wh-yellow text-center" data-id="element-1496">
              <div className="w-16 h-16 bg-wh-yellow rounded-full flex items-center justify-center text-gray-900 text-2xl font-bold mx-auto mb-4" data-id="element-1497">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1498">
                Bring It to Life
              </h3>
              <p className="text-gray-700" data-id="element-1499">
                We produce your designs in-house with our printing and
                embroidery services.
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 text-center text-white" data-id="element-1500">
            <Sparkles className="w-16 h-16 mx-auto mb-6" data-id="element-1501" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1502">
              Let's Create Magic Together!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-1503">
              No matter what you envision, our team is ready to turn your ideas
              into stunning visuals. Contact us today, and let's get started on
              your next graphic design adventure!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1504">
              <a href="/Contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-wh-pink font-bold rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1505">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1506">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-id="element-1507">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1508">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center" data-id="element-1509">
            Graphic Design FAQs
          </h2>

          <div className="space-y-4" data-id="element-1510">
            {[{
            question: 'What graphic design services do you offer?',
            answer: 'We offer a full range of graphic design services including logo design and refinement, brand identity development, custom apparel graphics, event banners, promotional materials (flyers, brochures, business cards), marketing collateral, and social media graphics. Everything is done in-house by our talented design team.'
          }, {
            question: 'Can you help if I already have a logo but need it refined?',
            answer: 'Absolutely! Whether you need a complete logo redesign, want to modernize an existing logo, or just need minor adjustments for better visibility and print quality, our design team can help spruce up your existing branding.'
          }, {
            question: "What if I'm starting from scratch with no design ideas?",
            answer: "No problem! Our designers are here to guide you through the entire creative process. We'll ask the right questions about your brand, target audience, and goals, then develop concepts that bring your vision to life. We work with you every step of the way."
          }, {
            question: 'How long does the design process take?',
            answer: "Timeline varies depending on project complexity. Simple logo refinements may take a few days, while comprehensive branding packages can take 1-2 weeks. We'll provide a clear timeline during our initial consultation and keep you updated throughout the process."
          }, {
            question: 'Can you design for both print and digital?',
            answer: "Yes! Our designs are optimized for whatever medium you need—whether it's screen printing, embroidery, digital marketing, social media, or traditional print materials. We ensure your designs look great everywhere."
          }, {
            question: 'Do you provide the final design files?',
            answer: 'Yes! Once your design is finalized and approved, we provide you with all the necessary file formats for your needs—including high-resolution files suitable for print, web-optimized versions, and vector files when applicable.'
          }, {
            question: "Can you produce the designs after they're created?",
            answer: "Absolutely! That's the beauty of our one-stop shop. Once your design is approved, we can immediately produce it using our in-house screen printing, embroidery, DTG, laser etching, and other services. No need to coordinate with multiple vendors!"
          }, {
            question: 'How much do graphic design services cost?',
            answer: 'Pricing varies based on project scope and complexity. Simple logo refinements start at one rate, while comprehensive branding packages are priced differently. Contact us for a free consultation and custom quote tailored to your specific needs and budget.'
          }, {
            question: 'What do I need to provide to get started?',
            answer: "Just bring your ideas! We'll guide you through the rest. If you have existing brand materials, logos, color preferences, or inspiration examples, those are helpful but not required. Our designers will work with whatever you have to create something amazing."
          }].map((faq, index) => <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1511">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-8 py-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-1512">
                  <h3 className="text-lg font-bold text-gray-900 pr-4" data-id="element-1513">
                    {faq.question}
                  </h3>
                  <ChevronDown className={`w-6 h-6 text-wh-pink flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-1514" />
                </button>
                {openFaq === index && <div className="px-8 pb-6" data-id="element-1515">
                    <p className="text-gray-700 leading-relaxed" data-id="element-1516">
                      {faq.answer}
                    </p>
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1517" />
    </div>;
}